// ─── NEWS SERVICE — three-API financial news aggregation ──────────────────────
// NewsData.io (primary — ticker search + sentiment) · GNews (quality outlets) ·
// Currents API (volume + recency). In-memory cache, 15-minute TTL per key.
// Every article — regardless of source — is normalized to a single shape:
//   { id, title, source, sourceApi, url, publishedAt, ageLabel,
//     sentiment, sentimentScore, category, ticker, summary }
// No backend, no telemetry — direct browser fetch to each API. Any network or
// parse failure is swallowed and returns [] so callers never throw.

const TTL = 15 * 60 * 1000; // 15 minutes

// Source quality ranking for dedup tie-breaking: Reuters > Bloomberg > AP >
// others (1) > unknown (0).
const SOURCE_RANK = {
  reuters:              4,
  bloomberg:            3,
  ap:                   2,
  "associated press":   2,
};

// Signal type → search query. Falls through to a generic financial query.
const SIGNAL_QUERY = {
  "DIVERGENCE":     "VIX volatility hedge fund options",
  "MACRO":          "Federal Reserve yield curve recession",
  "OPTIONS FLOW":   "options unusual activity institutional",
  "ENERGY":         "crude oil petroleum supply OPEC",
  "PHYSICAL WORLD": "shipping sanctions tanker crude",
  "SQUEEZE":        "short interest short squeeze",
  "DARK POOL":      "institutional block trade dark pool",
  "REGULATORY":     "SEC enforcement congressional trading STOCK Act",
  "FEAR REGIME":    "market volatility fear selloff",
};

// Category → search query for the main feed.
function buildQuery(category) {
  switch (category) {
    case "macro":        return "Federal Reserve OR yield curve OR recession OR inflation";
    case "energy":       return "crude oil OR petroleum OR OPEC OR natural gas";
    case "crypto":       return "cryptocurrency OR blockchain OR bitcoin";
    case "earnings":     return "earnings OR revenue OR guidance OR quarterly";
    case "geopolitical": return "sanctions OR geopolitical OR war OR conflict trade";
    case "all":
    case "markets":
    default:             return "finance OR markets OR business OR economy";
  }
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function hashStr(str) {
  // Simple deterministic string hash — used for article ids from url.
  let h = 0;
  const s = str || "";
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return "a" + Math.abs(h).toString(36);
}

function parseDate(str) {
  const t = Date.parse(str);
  return isNaN(t) ? Date.now() : t;
}

function ageLabel(ts) {
  if (!ts) return "";
  const diff = Date.now() - ts;
  if (diff < 60000) return "just now";
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
}

const STOP = new Set([
  "the", "a", "an", "and", "or", "for", "to", "of", "in", "on", "at", "by",
  "is", "are", "as", "with", "from", "its", "after", "over", "amid", "new",
  "how", "why", "what", "this", "that",
]);

// Keyword sentiment for GNews/Currents (NewsData.io provides its own).
const POS = new Set([
  "surge", "surges", "rally", "rallies", "gain", "gains", "beat", "beats",
  "record", "soar", "soars", "jump", "jumps", "upgrade", "growth", "profit",
  "profits", "boom", "strong", "optimism", "recovery", "rebound", "outperform",
  "bullish", "rise", "rises", "higher", "boost", "win", "wins", "expansion",
  "robust", "climb", "climbs", "up",
]);
const NEG = new Set([
  "crash", "crashes", "plunge", "plunges", "fall", "falls", "miss", "misses",
  "loss", "losses", "slump", "slumps", "drop", "drops", "downgrade", "recession",
  "fear", "fears", "selloff", "weak", "bearish", "default", "crisis", "layoff",
  "layoffs", "cut", "cuts", "decline", "declines", "warn", "warns", "warning",
  "tumble", "tumbles", "sink", "sinks", "lower", "slowdown", "risk", "risks",
  "collapse", "volatile", "volatility", "sanctions", "war", "down", "slide",
]);

function keywordSentiment(text) {
  const words = (text || "").toLowerCase().split(/[^a-z]+/);
  const set = new Set(words);
  let pos = 0, neg = 0;
  for (const w of set) {
    if (POS.has(w)) pos++;
    if (NEG.has(w)) neg++;
  }
  const total = pos + neg;
  if (total === 0) return { sentiment: "neutral", score: 0 };
  const score = Math.max(-1, Math.min(1, (pos - neg) / total));
  const sentiment = score > 0.15 ? "positive" : score < -0.15 ? "negative" : "neutral";
  return { sentiment, score };
}

function hostname(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); }
  catch { return ""; }
}

// ─── NEWS SERVICE ─────────────────────────────────────────────────────────────
class NewsService {
  constructor() {
    this.cache = new Map(); // key → { ts, value }
  }

  // ── Cache ──
  _get(key) {
    const e = this.cache.get(key);
    if (e && Date.now() - e.ts < TTL) return e.value;
    return null;
  }
  _set(key, value) {
    this.cache.set(key, { ts: Date.now(), value });
  }

  // ── Normalizers ──
  _normNewsData(r, category, ticker) {
    const text = `${r.title || ""} ${r.description || ""}`;
    let sentiment, score;
    if (r.sentiment && (r.sentiment === "positive" || r.sentiment === "neutral" || r.sentiment === "negative")) {
      sentiment = r.sentiment;
      const st = r.sentiment_stats;
      if (st && typeof st.positive === "number" && typeof st.negative === "number") {
        score = Math.max(-1, Math.min(1, (st.positive - st.negative) / 100));
      } else {
        score = sentiment === "positive" ? 0.5 : sentiment === "negative" ? -0.5 : 0;
      }
    } else {
      ({ sentiment, score } = keywordSentiment(text));
    }
    const ts = parseDate(r.pubDate);
    return {
      id:             hashStr(r.link),
      title:          r.title || "",
      source:         r.source_id || r.source_name || "unknown",
      sourceApi:      "NewsData.io",
      url:            r.link || "",
      publishedAt:    ts,
      ageLabel:       ageLabel(ts),
      sentiment,
      sentimentScore: score,
      category,
      ticker:         ticker || null,
      summary:        (r.description || "").slice(0, 150),
    };
  }

  _normGNews(a, category, ticker) {
    const { sentiment, score } = keywordSentiment(`${a.title || ""} ${a.description || ""}`);
    const ts = parseDate(a.publishedAt);
    return {
      id:             hashStr(a.url),
      title:          a.title || "",
      source:         a.source?.name || "unknown",
      sourceApi:      "GNews",
      url:            a.url || "",
      publishedAt:    ts,
      ageLabel:       ageLabel(ts),
      sentiment,
      sentimentScore: score,
      category,
      ticker:         ticker || null,
      summary:        (a.description || "").slice(0, 150),
    };
  }

  _normCurrents(a, category, ticker) {
    const { sentiment, score } = keywordSentiment(`${a.title || ""} ${a.description || ""}`);
    const ts = parseDate(a.published);
    return {
      id:             hashStr(a.url || a.id),
      title:          a.title || "",
      source:         hostname(a.url) || "unknown",
      sourceApi:      "Currents API",
      url:            a.url || "",
      publishedAt:    ts,
      ageLabel:       ageLabel(ts),
      sentiment,
      sentimentScore: score,
      category,
      ticker:         ticker || null,
      summary:        (a.description || "").slice(0, 150),
    };
  }

  // ── Per-API fetchers (each returns [] on any failure) ──
  async _fetchNewsData(query, key, category, ticker, catLabel) {
    if (!key) return [];
    try {
      const url =
        `https://newsdata.io/api/1/news?apikey=${encodeURIComponent(key)}` +
        `&language=en&q=${encodeURIComponent(query)}` +
        (category ? `&category=${category}` : "");
      const res = await fetch(url);
      const data = await res.json();
      return (data?.results || [])
        .map((r) => this._normNewsData(r, catLabel, ticker))
        .filter((a) => a.title && a.url);
    } catch { return []; }
  }

  async _fetchGNews(query, key, ticker, catLabel) {
    if (!key) return [];
    try {
      const url =
        `https://gnews.io/api/v4/search?q=${encodeURIComponent(query)}` +
        `&lang=en&max=10&apikey=${encodeURIComponent(key)}`;
      const res = await fetch(url);
      const data = await res.json();
      return (data?.articles || [])
        .map((a) => this._normGNews(a, catLabel, ticker))
        .filter((a) => a.title && a.url);
    } catch { return []; }
  }

  async _fetchCurrents(query, key, ticker, catLabel) {
    if (!key) return [];
    try {
      const url =
        `https://api.currentsapi.services/v1/search?keywords=${encodeURIComponent(query)}` +
        `&language=en&apiKey=${encodeURIComponent(key)}`;
      const res = await fetch(url);
      const data = await res.json();
      return (data?.news || [])
        .map((a) => this._normCurrents(a, catLabel, ticker))
        .filter((a) => a.title && a.url);
    } catch { return []; }
  }

  // ── Dedup ──
  _sourceScore(a) {
    const src = (a.source || "").toLowerCase();
    for (const k in SOURCE_RANK) if (src.includes(k)) return SOURCE_RANK[k];
    return a.source && a.source !== "unknown" ? 1 : 0;
  }
  _titleTokens(t) {
    return new Set(
      (t || "").toLowerCase().split(/[^a-z0-9]+/)
        .filter((w) => w.length > 2 && !STOP.has(w))
    );
  }
  _overlap(a, b) {
    const A = this._titleTokens(a), B = this._titleTokens(b);
    if (!A.size || !B.size) return 0;
    let inter = 0;
    for (const w of A) if (B.has(w)) inter++;
    return inter / Math.min(A.size, B.size);
  }
  // Deduplicate by title — if two share >60% of words, keep the better source.
  _dedupe(list) {
    const kept = [];
    for (const art of list) {
      if (!art.title) continue;
      let dup = -1;
      for (let i = 0; i < kept.length; i++) {
        if (this._overlap(art.title, kept[i].title) > 0.6) { dup = i; break; }
      }
      if (dup === -1) kept.push(art);
      else if (this._sourceScore(art) > this._sourceScore(kept[dup])) kept[dup] = art;
    }
    return kept;
  }

  // ── Public methods ──
  async fetchFinancialNews(keys, category = "all") {
    const cacheKey = `news_${category}`;
    const cached = this._get(cacheKey);
    if (cached) return cached;

    const query = buildQuery(category);
    const [nd, gn, cu] = await Promise.all([
      this._fetchNewsData(query, keys?.newsdataKey, "business", null, category),
      this._fetchGNews(query, keys?.gnewsKey, null, category),
      this._fetchCurrents(query, keys?.currentsKey, null, category),
    ]);

    let all = this._dedupe([...nd, ...gn, ...cu]);
    all.sort((a, b) => b.publishedAt - a.publishedAt);
    all = all.slice(0, 40);

    this._set(cacheKey, all);
    return all;
  }

  async fetchTickerNews(ticker, keys) {
    const cacheKey = `ticker_${ticker}`;
    const cached = this._get(cacheKey);
    if (cached) return cached;

    // NewsData.io primary — ticker symbol as query. GNews secondary.
    const [nd, gn] = await Promise.all([
      this._fetchNewsData(ticker, keys?.newsdataKey, null, ticker, "ticker"),
      this._fetchGNews(ticker, keys?.gnewsKey, ticker, "ticker"),
    ]);

    let all = this._dedupe([...nd, ...gn]);
    all.sort((a, b) => b.publishedAt - a.publishedAt);
    all = all.slice(0, 8);

    this._set(cacheKey, all);
    return all;
  }

  async fetchSignalNews(signalType, keys) {
    const cacheKey = `signal_${signalType}`;
    const cached = this._get(cacheKey);
    if (cached) return cached;

    // NewsData.io only — preserve GNews/Currents daily budget.
    const query = SIGNAL_QUERY[signalType] || "financial markets institutional";
    const nd = await this._fetchNewsData(query, keys?.newsdataKey, null, null, "signal");

    const all = this._dedupe(nd)
      .sort((a, b) => b.publishedAt - a.publishedAt)
      .slice(0, 3);

    this._set(cacheKey, all);
    return all;
  }

  computeOverallSentiment(articles) {
    if (!articles || articles.length === 0) {
      return { score: 0, label: "neutral", negativeCount: 0, positiveCount: 0, neutralCount: 0 };
    }
    let sum = 0, neg = 0, pos = 0, neu = 0;
    for (const a of articles) {
      sum += a.sentimentScore || 0;
      if (a.sentiment === "negative") neg++;
      else if (a.sentiment === "positive") pos++;
      else neu++;
    }
    const score = sum / articles.length;
    const label = score > 0.15 ? "positive" : score < -0.15 ? "negative" : "neutral";
    return { score, label, negativeCount: neg, positiveCount: pos, neutralCount: neu };
  }
}

// ─── SINGLETON ────────────────────────────────────────────────────────────────
export const newsService = new NewsService();
